import React, { forwardRef, useMemo } from "react";
import { Tooltip } from "antd";
import "./InputBox.scss";
import truckTracker from "assets/images/map.svg";
import TickCircel from "assets/images/tickCircle.svg";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { BsSearch, BsTelephone } from "react-icons/bs";
import { FiMail } from "react-icons/fi";
import { RiLightbulbFlashFill } from "react-icons/ri";

const mergeRefs = (forwardedRef, registerRef) => (node) => {
  try {
    if (registerRef) {
      if (typeof registerRef === "function") registerRef(node);
      else if (typeof registerRef === "object") registerRef.current = node;
    }
    if (forwardedRef) {
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (typeof forwardedRef === "object") forwardedRef.current = node;
    }
  } catch (e) {
    console.warn("InputBox: mergeRefs failed silently", e);
  }
};

// ✅ Safe extraction — never enumerates RHF proxy, try/catch for backend failure
const safeExtractRegister = (register) => {
  if (!register || typeof register !== "object") {
    return { registerRef: undefined, registerRest: {} };
  }
  try {
    return {
      registerRef: register.ref,
      registerRest: {
        name:     register.name     ?? undefined,
        onBlur:   register.onBlur   ?? undefined,
        onChange:  register.onChange  ?? undefined,
      },
    };
  } catch (e) {
    console.warn("InputBox: register extraction failed silently", e);
    return { registerRef: undefined, registerRest: {} };
  }
};

const InputBox = forwardRef((props, forwardedRef) => {
  const {
    id,
    placeholder = "",
    register,
    value = "",
    type = "",
    onChange,
    name = "",
    className = "",
    grayedBox = false,
    profile = false,
    shadow = false,
    disabled = false,
    maxLength,
    SearchIcon = false,
    confirm = false,
    isOtp = false,
    Message = false,
    Password = false,
    call = false,
    setPassword,
    showPassword = false,
    notesBox = false,
    searchBox = false,
    proposal = false,
    sendOtp,
    supplierOTP,
    memberOtp,
    agentOtp,
    customerOtp,
    supplierOtp,
    onSubmitExist,
    onKeyDown,
    quantity,
    ISpanValidGreen,
    ISpanValidRed,
    upperCaseLabel = false,
    isTracker = false,
    hideTickMark = false,
    inputError = false,
    disabledOtpBtn = false,
    isDynamic = false,
    autoFocus = false,
    isBulbIcon = false,
  } = props;

  // ✅ Safe extraction — never crashes even if backend/RHF fails
  const { registerRef, registerRest } = useMemo(
    () => safeExtractRegister(register),
    [register]
  );

  // ✅ Merged ref stabilized with useMemo
  const mergedRef = useMemo(
    () => mergeRefs(forwardedRef, registerRef),
    [forwardedRef, registerRef]
  );

  // ✅ Safe onChange handler — handles both RHF and plain onChange
  const handleChange = (e) => {
    try {
      if (onChange) onChange(e);
    } catch (err) {
      console.warn("InputBox: onChange failed", err);
    }
    try {
      if (registerRest?.onChange) registerRest.onChange(e);
    } catch (err) {
      console.warn("InputBox: registerRest.onChange failed", err);
    }
  };

  const handleKeyDown = (e) => {
    try {
      if (type === "number" && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
        e.preventDefault();
      }
      if (onKeyDown) onKeyDown(e);
    } catch (err) {
      console.warn("InputBox: onKeyDown failed", err);
    }
  };

  // ✅ Reusable className builder
  const resolveClassName = () =>
    [
      className,
      disabled
        ? "inputBoxGrayed"
        : grayedBox
        ? "inputBoxGrayed"
        : shadow
        ? "inputBoxIconStyle"
        : notesBox
        ? "NotesinputBoxStyle"
        : searchBox
        ? "searchBoxStyle"
        : ISpanValidRed
        ? "inputBoxStyle invalidRedBorder shakeInput"
        : profile
        ? "inputBoxStyleProfile"
        : "inputBoxStyle",
      upperCaseLabel ? "uppercaseText" : "",
      inputError ? "inputfiledError" : "",
    ]
      .filter(Boolean)
      .join(" ");

  // ✅ Reusable icon renderer
  const renderIcon = (includeOtpVariant = false) => {
    if (ISpanValidGreen)
      return (
        <div className="search_icon">
          <img src={TickCircel} alt="tick" />
        </div>
      );
    if (ISpanValidRed) return <></>;
    if (SearchIcon)
      return (
        <i className="search_icon">
          <BsSearch color="grey" />
        </i>
      );
    if (confirm)
      return (
        <div className="search_icon">
          <span>Confirm</span>
        </div>
      );
    if (isOtp) {
      const otpVerified = includeOtpVariant
        ? customerOtp || supplierOtp || memberOtp || supplierOTP || agentOtp
        : customerOtp || supplierOtp || memberOtp || agentOtp;
      return (
        <div className="search_icon">
          {otpVerified ? (
            <img src={hideTickMark ? null : TickCircel} alt="tick" />
          ) : (
            <span onClick={() => (!disabledOtpBtn && onSubmitExist ? onSubmitExist() : null)}>
              Send OTP
            </span>
          )}
        </div>
      );
    }
    if (Message)
      return (
        <i className="message_icon">
          <FiMail />
        </i>
      );
    if (call)
      return (
        <i className="message_icon">
          <BsTelephone size={20} />
        </i>
      );
    if (Password)
      return (
        <i className="password_icon">
          <span onClick={() => setPassword && setPassword(!showPassword)}>
            {!showPassword ? (
              <AiOutlineEye style={{ fontSize: "20px" }} />
            ) : (
              <AiOutlineEyeInvisible style={{ fontSize: "20px" }} />
            )}
          </span>
        </i>
      );
    if (quantity)
      return (
        <div className="metric_wrapper">
          <label className="metric_tons">Metric Tons</label>
        </div>
      );
    if (isTracker)
      return (
        <div className="message_icon">
          <img src={truckTracker} alt="track" />
        </div>
      );
    if (isBulbIcon)
      return (
        <Tooltip
          className="search_icon"
          title="Values must follow the selected ratio format (e.g., 1:0, 1:0.5)."
        >
          <span>
            <RiLightbulbFlashFill size={20} color="#0066b3" />
          </span>
        </Tooltip>
      );
    return null;
  };

  // ✅ Shared input props to avoid repetition
  const commonInputProps = {
    ref: mergedRef,
    disabled,
    name,
    id,
    placeholder,
    type,
    maxLength,
    autoComplete: "off",
    onWheel: (e) => e.currentTarget.blur(),
    ...registerRest,  // ✅ safe extracted RHF props (never enumerates proxy)
  };

  return (
    <>
      {/* ───── NOTES / TEXTAREA ───── */}
      {notesBox ? (
        <div>
          <textarea
            {...commonInputProps}
            className={disabled ? "inputBoxGrayed" : "NotesinputBoxStyle"}
            defaultValue={value}
            onChange={handleChange}
          />
        </div>

      /* ───── PROPOSAL INPUT ───── */
      ) : proposal ? (
        <div className="input_head">
          <input
            {...commonInputProps}
            className={resolveClassName()}
            defaultValue={value}
            onKeyDown={onKeyDown}
            onChange={onChange} // proposal keeps its own direct onChange
          />
          <div>{renderIcon(false)}</div>
        </div>

      /* ───── DYNAMIC (controlled) INPUT ───── */
      ) : isDynamic ? (
        <div className="input_head">
          <input
            {...commonInputProps}
            className={resolveClassName()}
            value={value}           // controlled
            onKeyDown={onKeyDown}
            onChange={handleChange}
          />
        </div>

      /* ───── DEFAULT INPUT ───── */
      ) : (
        <div className="input_head">
          <input
            {...commonInputProps}
            className={resolveClassName()}
            defaultValue={value}    // uncontrolled
            autoFocus={autoFocus}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
          />
          <div>{renderIcon(true)}</div>
        </div>
      )}
    </>
  );
});

InputBox.displayName = "InputBox";
export default InputBox;